import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	FunctionComponent,
	PropsWithChildren,
	ReactElement,
	useCallback,
	useEffect,
	useMemo
} from "react";
import { setHttpError } from "../ErrorPage/errorStore";
import { AddressBookContactSource } from "./Overview/Overview";
import { GlobalNavigationSidebar } from "../Layout/GlobalNavigationSidebar/GlobalNavigationSidebar";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import {
	SourceOption,
	SourceSelector,
	SourceSelectorValue,
	useAvailableSourceOptions
} from "./SourceSelector";
import {
	ExchangeAvailability,
	useExchangeAvailability
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import {
	getAddressBookNavigationForOverview,
	tryParseContactSource
} from "./routing";
import { useHandleNavigationInGlobalSubNavigation } from "../Layout/GlobalNavigationSidebar/useGlobalNavigationVisibilityState";
import {
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter
} from "client/Navigation";
import { GlobalNavigationCriteria, useGlobalNavigationStore } from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import { usePageTitle } from "client/Hooks/usePageTitle";
import { EmailNotConfiguredDialog } from "../Email/Dialogs/EmailNotConfiguredDialog";
import styles from "./AddressBookLayout.module.less";
import { persistLastSelectedSource } from "./AddressBookSelectSource";
import { useContactSourceValue } from "./Overview/useContactSourceValue";
import { useAnalyticsStore } from "../Analytics/analyticsStore";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { ContactSource } from "client/Api/Email/contacts";
import { never } from "client_legacy/ndaw/errors";

export type AddressBookLayoutProps = {
	source?: AddressBookContactSource;
	department?: string;
	officeLocation?: string;
	onNavigate: NavigationCallback;
};

export const AddressBookLayout = (
	props: PropsWithChildren<AddressBookLayoutProps>
): ReactElement | null => {
	const {
		source: pathSource,
		onNavigate,
		children,
		department,
		officeLocation
	} = props;

	const { addressBook } = userConfiguration();

	usePageTitle(localize("_AddressBook"));

	const exchangeAvailability: ExchangeAvailability = useExchangeAvailability();
	const availableSourceOptions: readonly SourceOption[] = useAvailableSourceOptions(exchangeAvailability);

	const setActiveGlobalNavigationCriteria: (criteria: GlobalNavigationCriteria) => () => void = useGlobalNavigationStore(
		x => x.setActiveCriteria
	);

	const handleNavigationInGlobalSubNavigation: () => void = useHandleNavigationInGlobalSubNavigation();

	const sourceSelectorValue: SourceSelectorValue | undefined = useContactSourceValue(
		pathSource,
		department,
		officeLocation
	);

	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);

	useEffect(
		(): (() => void) => {
			addAnalyticsAppEvent({
				appType: AppType.AddressBook
			});

			return setActiveGlobalNavigationCriteria({
				type: "app",
				appType: AppType.AddressBook
			});
		},
		[setActiveGlobalNavigationCriteria, addAnalyticsAppEvent]
	);

	const changeSourceSelectorValueHandler = useCallback(
		(value: SourceSelectorValue): void => {
			persistLastSelectedSource(value.source);

			handleNavigationInGlobalSubNavigation();

			let navigation: Navigation;

			switch (value.source) {
				case ContactSource.Personal:
				case ContactSource.Gal:
					navigation = getAddressBookNavigationForOverview({
						source: value.source
					});
					break;
				case ContactSource.Workspace:
					navigation = getAddressBookNavigationForOverview({
						source: value.source,
						filter: value.filter
					});
					break;
				default:
					never(value);
			}

			onNavigate(navigation);
		},
		[
			handleNavigationInGlobalSubNavigation,
			onNavigate
		]
	);

	const closeEmailNotConfigureDialogHandler = useMemo(
		(): (() => void) | undefined => {
			const sourceToSelect: AddressBookContactSource | undefined = availableSourceOptions[0]
				?.source;

			if (isNil(sourceToSelect)) {
				return undefined;
			}

			return (): void => changeSourceSelectorValueHandler({ source: sourceToSelect });
		},
		[availableSourceOptions, changeSourceSelectorValueHandler]
	);

	const isUnavailableSourceSelected: boolean = useMemo(
		(): boolean => {
			if (!sourceSelectorValue) {
				return false;
			}

			return availableSourceOptions.every(x => x.source !== sourceSelectorValue.source);
		},
		[sourceSelectorValue, availableSourceOptions]
	);

	if (!addressBook.isAppAvailable) {
		setHttpError({
			statusCode: 403
		});

		return null;
	}

	return (
		<>
			<GlobalNavigationSidebar.ActivePanelOverride
				title={localize("_AddressBook")}
				kind={AppType.AddressBook}
			>
				<SourceSelector
					options={availableSourceOptions}
					value={sourceSelectorValue}
					onChange={changeSourceSelectorValueHandler}
				/>
			</GlobalNavigationSidebar.ActivePanelOverride>

			{isUnavailableSourceSelected && (
				<>
					{isNil(closeEmailNotConfigureDialogHandler)
						? (
							<EmailNotConfiguredDialog
								shouldNavigateBackToWorkspaceOnClose
								hideConfigurationSuggestion={exchangeAvailability !== ExchangeAvailability.RequiresConfiguration}
							/>
						): (
							<EmailNotConfiguredDialog
								shouldNavigateBackToWorkspaceOnClose={false}
								hideConfigurationSuggestion={exchangeAvailability !== ExchangeAvailability.RequiresConfiguration}
								onClose={closeEmailNotConfigureDialogHandler}
							/>
						)
					}
				</>
			)}
			<div className={styles.root}>
				{!isUnavailableSourceSelected && children}
			</div>
		</>
	);
};
AddressBookLayout.displayName = "AddressBookLayout";

export const AddressBookLayoutForRouter: FunctionComponent<PropsWithChildren<{}>> = connectToRouter(
	AddressBookLayout,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback,
		"source": {
			isRequired: false,
			tryParse: tryParseContactSource
		},
		"department": {
			isRequired: false,
			type: "query"
		},
		"officeLocation": {
			isRequired: false,
			type: "query"
		}
	}
);